import { Outlet, NavLink, useLocation, Navigate } from 'react-router-dom';
import { Home, Target, User, Crown, Activity, Clock, Sparkles, ChevronLeft, Gem, Coins } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import LanguageSwitcher from './LanguageSwitcher';
import TrialNudge from './TrialNudge';

// True only inside the native iOS/Android (Capacitor) wrapper. Used to show a
// "back to Live Scores" control, since the native app launches into the blue
// Live Scores experience and users otherwise have no way back from Predictions.
const isNativePlatform = () =>
  typeof window !== 'undefined'
  && window.Capacitor
  && typeof window.Capacitor.isNativePlatform === 'function'
  && window.Capacitor.isNativePlatform();

const TrialBanner = ({ user }) => {
  if (!user || user.subscription_status !== 'trial' || !user.trial_expires_at) return null;
  const expiresAt = new Date(user.trial_expires_at);
  const now = new Date();
  const msLeft = expiresAt - now;
  if (msLeft <= 0) return null;
  const daysLeft = Math.ceil(msLeft / (1000 * 60 * 60 * 24));
  const tone = daysLeft <= 2 ? 'from-amber-500/20 to-amber-500/5 border-amber-500/40 text-amber-300' : 'from-emerald-500/15 to-emerald-500/5 border-emerald-500/30 text-emerald-300';
  return (
    <div className={`bg-gradient-to-r ${tone} border-b px-4 py-2`} data-testid="trial-banner">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-xs sm:text-sm">
          <Clock className="w-4 h-4 shrink-0" />
          <span className="font-semibold">
            {daysLeft === 1 ? 'Last day of your free trial' : `${daysLeft} days left on your free trial`}
          </span>
          <span className="hidden sm:inline text-zinc-400">· Add a card to keep your Pro access</span>
        </div>
        <NavLink to="/app/subscription" className="text-xs sm:text-sm font-bold underline-offset-2 hover:underline shrink-0" data-testid="trial-banner-upgrade">
          Upgrade →
        </NavLink>
      </div>
    </div>
  );
};

const TrialExpiredNotice = () => (
  <div className="bg-amber-500/15 border-b border-amber-500/40 px-4 py-2 text-amber-300" data-testid="trial-expired-banner">
    <div className="max-w-7xl mx-auto flex items-center justify-between gap-3 text-xs sm:text-sm">
      <span className="font-semibold">Your trial has ended — add a card to keep your AI picks</span>
      <NavLink to="/app/subscription" className="font-bold underline-offset-2 hover:underline shrink-0">Upgrade →</NavLink>
    </div>
  </div>
);

const Layout = () => {
  const location = useLocation();
  const { user } = useAuth();
  const native = isNativePlatform();

  // Free + trial_expired users can only access subscription/privacy/terms.
  const restrictedAllowedPaths = ['/app/subscription', '/app/subscription/success', '/app/privacy', '/app/terms'];
  const isAllowed = restrictedAllowedPaths.some(p => location.pathname.startsWith(p));
  const isRestricted = user && (user.subscription_status === 'free' || user.subscription_status === 'trial_expired') && !user.is_admin;

  if (isRestricted && !isAllowed) {
    return <Navigate to="/app/subscription" replace state={{ reason: user.subscription_status }} />;
  }

  const navItems = [
    { path: '/app', icon: Home, label: 'Home' },
    { path: '/app/picks', icon: Target, label: 'Picks' },
    { path: '/app/value-bets', icon: Gem, label: 'Value' },
    { path: '/app/odds', icon: Coins, label: 'Odds' },
    { path: '/app/live-scores', icon: Activity, label: 'Live' },
    { path: '/app/vip-picks', icon: Crown, label: 'VIP', vipOnly: false },
    { path: '/app/profile', icon: User, label: 'Profile' },
  ];

  return (
    <div className="min-h-screen bg-[#070b14] pb-20">
      {/* Trial banner (above header) */}
      <TrialBanner user={user} />
      {user?.subscription_status === 'trial_expired' && !user.is_admin && <TrialExpiredNotice />}

      {/* Header */}
      <header className="sticky top-0 z-40 bg-zinc-900/50 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            {native && (
              <NavLink
                to="/live-scores"
                className="flex items-center gap-0.5 -ml-1 pr-1 py-1 text-zinc-300 hover:text-white transition-colors"
                aria-label="Back to Live Scores"
                data-testid="back-to-live-scores"
              >
                <ChevronLeft className="w-6 h-6" />
                <span className="text-[11px] font-bold leading-none">Scores</span>
              </NavLink>
            )}
            <NavLink to="/app" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center">
                <Target className="w-5 h-5 text-white" />
              </div>
              <div>
                <span className="text-xl font-bold tracking-tight block leading-tight">SMARTKICK</span>
                <span className="text-[10px] text-zinc-500 tracking-wide">AI-Powered Match Predictions</span>
              </div>
            </NavLink>
          </div>

          <div className="flex items-center gap-3">
            {user?.is_partner && (
              <NavLink
                to="/app/partner-dashboard"
                className="hidden sm:inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/25 transition-colors"
                data-testid="header-partner-link"
              >
                <Sparkles className="w-3 h-3" /> Partner
              </NavLink>
            )}
            {user?.is_vip && (
              <span className="vip-badge">
                <Crown className="w-3 h-3" />
                VIP
              </span>
            )}
            {user?.subscription_status === 'trial' && !user.is_vip && (
              <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30" data-testid="trial-badge">
                TRIAL
              </span>
            )}
            <LanguageSwitcher compact />
            {!user && (
              <NavLink
                to="/login"
                className="text-sm text-zinc-400 hover:text-white transition-colors"
                data-testid="header-login-btn"
              >
                Sign In
              </NavLink>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        <TrialNudge />
        <Outlet />
      </main>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-[#070b14]/95 backdrop-blur-xl border-t border-white/10 z-50 pb-safe">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-around py-2">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `nav-item ${isActive ? 'active' : ''}`
                }
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="text-xs">{item.label}</span>
              </NavLink>
            ))}
          </div>
        </div>
      </nav>
    </div>
  );
};

export default Layout;
